import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, MapPin, Calendar, DollarSign } from 'lucide-react';
import { api } from '../services/api';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPending = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (!user || user.role !== 'admin') {
                    navigate('/');
                    return;
                }
                const data = await api.getPendingListings();
                setListings(data);
            } catch (err) {
                setError('Failed to load pending listings');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchPending();
    }, [navigate]);

    const handleStatusUpdate = async (id, status) => {
        try {
            await api.updateListingStatus(id, status);
            setListings(listings.filter(item => item.id !== id)); // Remove from list
        } catch (err) {
            alert('Failed to update status');
        }
    };

    if (loading) return <div className="p-4 text-center">Loading...</div>;

    return (
        <div className="pb-20 bg-gray-50 min-h-screen">
            <header className="bg-white p-4 sticky top-0 z-10 shadow-sm flex items-center mb-4">
                <button onClick={() => navigate('/profile')} className="mr-3 p-2 rounded-full hover:bg-gray-100">
                    <ArrowLeft size={24} className="text-gray-600" />
                </button>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            </header>

            <div className="p-4 max-w-2xl mx-auto space-y-4">
                {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg">{error}</div>}

                {listings.length === 0 && (
                    <div className="text-center text-gray-500 py-10">
                        No pending listings to review.
                    </div>
                )}

                {listings.map(item => (
                    <div key={item.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                        <div className="p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold mb-1 ${item.type === 'event' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                        {item.type.toUpperCase()}
                                    </span>
                                    <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                                </div>
                            </div>

                            <p className="text-gray-600 text-sm mb-3">{item.description}</p>

                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-500 mb-4">
                                <div className="flex items-center">
                                    <MapPin size={14} className="mr-1" />
                                    {item.city}
                                </div>
                                {item.type === 'event' && (
                                    <div className="flex items-center">
                                        <Calendar size={14} className="mr-1" />
                                        {new Date(item.event_date).toLocaleDateString()}
                                    </div>
                                )}
                                {item.type === 'classified' && (
                                    <div className="flex items-center">
                                        <DollarSign size={14} className="mr-1" />
                                        {item.price || 'N/A'}
                                    </div>
                                )}
                            </div>

                            {item.image_urls && JSON.parse(item.image_urls).length > 0 && (
                                <img
                                    src={JSON.parse(item.image_urls)[0]}
                                    alt="Preview"
                                    className="w-full h-48 object-cover rounded-lg mb-4"
                                />
                            )}

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => handleStatusUpdate(item.id, 'approved')}
                                    className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 flex items-center justify-center"
                                >
                                    <Check size={18} className="mr-2" /> Approve
                                </button>
                                <button
                                    onClick={() => handleStatusUpdate(item.id, 'rejected')}
                                    className="flex-1 bg-red-100 text-red-600 py-2 rounded-lg font-bold hover:bg-red-200 flex items-center justify-center"
                                >
                                    <X size={18} className="mr-2" /> Reject
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminDashboard;
